import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Repeat, ShoppingCart, Loader2, Flame, CalendarClock } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Toast } from "./Toast";
import AutoReorderModal from "./AutoReorderModal";

/**
 * Renders the customer's most-frequently re-ordered products with a
 * one-click "Re-order this list" CTA. Hides itself silently when the
 * user is logged out or has no order history.
 *
 * Props:
 *   - variant: "card" (Account page card)  | "strip" (Homepage section)
 *   - limit: number of items to fetch (default 6)
 */
export const FrequentlyReordered = ({ variant = "card", limit = 6 }) => {
  const { user, token } = useAuth();
  const { addToCart } = useCart();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [toast, setToast] = useState(null);
  const [autoReorderTarget, setAutoReorderTarget] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!user || !token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/account/frequently-reordered?limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data?.items || []);
    } catch (err) {
      // Silent: component renders nothing if fetch fails
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        console.error("Failed to fetch re-order list:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [user, token, limit]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  if (!user || loading) return null;
  if (items.length === 0) return null;

  const handleAddOne = async (productId, qty) => {
    const ok = await addToCart(productId, qty);
    setToast({ message: ok ? "Added to cart" : "Could not add", type: ok ? "success" : "error" });
  };

  const handleReorderAll = async () => {
    setReordering(true);
    let added = 0;
    for (const it of items) {
      const stock = typeof it.stock === "number" ? it.stock : 0;
      if (stock <= 0) continue;
      const qty = Math.min(it.typical_quantity || 1, stock);
      const ok = await addToCart(it.id, qty);
      if (ok) added += 1;
    }
    setReordering(false);
    setToast({
      message: added > 0 ? `${added} item${added === 1 ? "" : "s"} added to your cart` : "Nothing to add — items may be out of stock",
      type: added > 0 ? "success" : "error",
    });
  };

  const containerClass = variant === "strip"
    ? "bg-white rounded-2xl border shadow-sm p-6 md:p-8"
    : "card p-6";

  return (
    <section className={containerClass} data-testid={`frequently-reordered-${variant}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#00a99d]/10 flex items-center justify-center">
            <Repeat className="w-5 h-5 text-[#00a99d]" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              {variant === "strip" ? "Re-order your essentials" : "Frequently re-ordered"}
            </h2>
            <p className="text-sm text-gray-500">Your most-ordered items, ready to add back to the cart.</p>
          </div>
        </div>
        <button
          onClick={handleReorderAll}
          disabled={reordering}
          className="inline-flex items-center gap-2 bg-[#00a99d] hover:bg-[#008c82] disabled:bg-gray-400 text-white font-semibold px-5 py-2.5 rounded-lg transition shadow"
          data-testid="reorder-all-btn"
        >
          {reordering ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
          {reordering ? "Adding…" : "Re-order this list"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {items.map((p) => {
          const stock = typeof p.stock === "number" ? p.stock : 0;
          const outOfStock = stock <= 0;
          const qty = p.typical_quantity || 1;
          const pinned = !!p.pinned_popular;
          return (
            <div
              key={p.id}
              className={`relative border rounded-lg p-3 flex flex-col bg-white hover:shadow transition ${pinned ? "border-amber-300 ring-1 ring-amber-200" : ""}`}
              data-testid={`reorder-card-${p.id}`}
            >
              {pinned && (
                <span
                  className="absolute -top-2 left-2 inline-flex items-center gap-1 bg-amber-400 text-amber-900 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full shadow"
                  data-testid={`pinned-badge-${p.id}`}
                >
                  <Flame className="w-3 h-3" />
                  Most Popular
                </span>
              )}
              <Link to={`/products/${p.id}`} className="block aspect-square bg-gray-50 rounded mb-2 overflow-hidden">
                <img
                  src={p.image_url || "https://via.placeholder.com/200"}
                  alt={p.name}
                  className="w-full h-full object-contain"
                />
              </Link>
              <Link to={`/products/${p.id}`} className="block">
                <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 hover:text-[#00a99d]">{p.name}</h3>
              </Link>
              <div className="mt-auto pt-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-base font-bold text-[#00a99d]">£{p.price.toFixed(2)}</span>
                  <span className="text-[11px] text-gray-500">{pinned ? "Promo pick" : `Usually × ${qty}`}</span>
                </div>
                <button
                  onClick={() => handleAddOne(p.id, qty)}
                  disabled={outOfStock}
                  className={`w-full mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-2 py-1.5 rounded-md transition ${
                    outOfStock
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-gray-900 hover:bg-black text-white"
                  }`}
                  data-testid={`reorder-add-${p.id}`}
                >
                  <ShoppingCart className="w-3.5 h-3.5" />
                  {outOfStock ? "Out of stock" : `Add × ${qty}`}
                </button>
                <button
                  onClick={() => setAutoReorderTarget({ product: p, qty })}
                  className="w-full mt-1.5 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold text-[#00a99d] hover:text-[#008c82] hover:bg-[#00a99d]/5 px-2 py-1 rounded transition"
                  data-testid={`reorder-auto-${p.id}`}
                >
                  <CalendarClock className="w-3 h-3" />
                  Auto-Reorder
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {autoReorderTarget && (
        <AutoReorderModal
          product={autoReorderTarget.product}
          defaultQty={autoReorderTarget.qty}
          onClose={() => setAutoReorderTarget(null)}
          onSuccess={() => setToast({ message: "Auto-Reorder activated", type: "success" })}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </section>
  );
};
